import { TrafficAllocationVariant } from '@app/react-common/components/traffic-allocation/TrafficAllocationShared';
import { AbTest } from '../../../../models/AbTest';
import { AbTestVariant } from '../../../../models/AbTestVariant';
import { AbTestWidgetType } from '../../../../models/AbTestCreationType';
import { CheckoutProfile } from '../../hooks/useCheckoutProfiles';

export const CONTROL_SLOT = 0;
export const TEST_SLOT = 1;
export const CHECKOUT_PROFILE_SLOTS = [
  { index: CONTROL_SLOT, label: 'Control checkout profile' },
  { index: TEST_SLOT, label: 'Test checkout profile' },
];
export const DEFAULT_CHECKOUT_PROFILE_PERCENTAGE = 50;
export const DELETED_CHECKOUT_PROFILE_NAME = 'Deleted';

const slotIndexFromVariantName = (name: string): number | undefined =>
  name === 'A' ? CONTROL_SLOT : name === 'B' ? TEST_SLOT : undefined;

const findCheckoutProfileWidget = (variant: AbTestVariant) =>
  variant.abTestWidgets?.find((widget) => widget.type === AbTestWidgetType.CHECKOUT_PROFILE);

const applyExistingVariant = (
  variant: TrafficAllocationVariant,
  existingVariant: AbTestVariant,
): TrafficAllocationVariant => ({
  ...variant,
  isSelected: true,
  percentageTraffic: existingVariant.percentageTraffic,
  apiId: existingVariant.id ?? null,
  widgetApiId: findCheckoutProfileWidget(existingVariant)?.id,
  slotIndex: slotIndexFromVariantName(existingVariant.name),
});

/**
 * Maps checkout profiles into traffic allocation variants, keeping saved variants whose profile no longer exists
 */
export function buildCheckoutProfileVariants(
  checkoutProfiles: CheckoutProfile[],
  existingAbTest: AbTest | undefined,
  mode: 'create' | 'edit' | 'view' | 'duplicate',
): TrafficAllocationVariant[] {
  const existingVariants = existingAbTest?.abTestVariants ?? [];
  const findExistingVariant = (profileId: number) =>
    existingVariants.find((variant) => {
      const widget = findCheckoutProfileWidget(variant);
      return widget && Number(widget.externalId) === Number(profileId);
    });

  const profileVariants = checkoutProfiles.map((profile, idx) => {
    const existingVariant = findExistingVariant(profile.id);
    const isPreselected = mode === 'create' && idx < CHECKOUT_PROFILE_SLOTS.length;
    const variant: TrafficAllocationVariant = {
      apiId: null,
      widgetExternalId: profile.id,
      name: profile.name,
      description: '',
      percentageTraffic: isPreselected ? DEFAULT_CHECKOUT_PROFILE_PERCENTAGE : 0,
      isSelected: isPreselected,
      slotIndex: isPreselected ? idx : undefined,
      widgetApiId: undefined,
    };
    return existingVariant ? applyExistingVariant(variant, existingVariant) : variant;
  });

  const deletedProfileVariants = existingVariants
    .map((variant) => ({ variant, widget: findCheckoutProfileWidget(variant) }))
    .filter(
      ({ widget }) => widget && !checkoutProfiles.some((profile) => Number(profile.id) === Number(widget.externalId)),
    )
    .map(({ variant, widget }) =>
      applyExistingVariant(
        {
          apiId: null,
          widgetExternalId: Number(widget.externalId),
          name: DELETED_CHECKOUT_PROFILE_NAME,
          description: '',
          percentageTraffic: 0,
          isSelected: false,
          widgetApiId: undefined,
        },
        variant,
      ),
    );

  return [...profileVariants, ...deletedProfileVariants];
}
